import React from 'react';
import { Helmet } from '@plone/volto/helpers';
import { flattenToAppURL } from '@plone/volto/helpers';

import {
  resolveContentImage,
  resolveHeroVisual,
  resolvePublicPageUrl,
} from '../helpers';

type Props = {
  content: any;
};

function resolveAbsoluteUrl(url?: string) {
  if (!url) return '';

  const flattened = flattenToAppURL(url);

  if (typeof window === 'undefined') {
    return flattened || url;
  }

  try {
    return new URL(flattened || url, window.location.origin).toString();
  } catch {
    return url;
  }
}

export default function ContentSeo({ content }: Props) {
  const title = content?.title || '';
  const description = content?.description || '';
  const pageUrl = resolvePublicPageUrl(content);

  const rawImage = resolveContentImage(content) || resolveHeroVisual(content);
  const imageUrl = resolveAbsoluteUrl(rawImage);

  if (!title) return null;

  return (
    <Helmet>
      <title>{title}</title>

      <meta name="theme-color" content="#C95F42" />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:type" content="article" />
      <meta property="og:url" content={pageUrl} />

      {imageUrl ? <meta property="og:image" content={imageUrl} /> : null}
      {imageUrl ? (
        <meta property="og:image:secure_url" content={imageUrl} />
      ) : null}
      {imageUrl ? <meta property="og:image:alt" content={title} /> : null}
      {imageUrl ? <meta property="og:image:type" content="image/jpeg" /> : null}
      {imageUrl ? <meta property="og:image:width" content="1200" /> : null}
      {imageUrl ? <meta property="og:image:height" content="630" /> : null}

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />

      {imageUrl ? <meta name="twitter:image" content={imageUrl} /> : null}
      {imageUrl ? <meta name="twitter:image:alt" content={title} /> : null}
    </Helmet>
  );
}
